import { VerificationStatus } from "shared/types";
import { has } from "shared/utils";
import { Loading02, Lock01 } from "untitledui-js-base";
import { AuditSummaryFlag } from "views/applications-id/components/ApplicantsTable/components/AuditSummaryFlag";
import { Cell } from "views/applications-id/components/ApplicantsTable/components/Cell";
import { ReportStatusBadge } from "views/applications-id/components/ApplicantsTable/components/ReportStatusBadge";
import { FormattedConvictions } from "views/applications-id/components/ApplicantsTable/components/TableBodySections/FormattedConvictions";
import {
  FullApplicant,
  Verification
} from "views/applications-id/components/ApplicantsTable/types";

export const CriminalReportSection = ({
  hasClearaCategorization,
  applicant,
  isApplicant,
  verification,
  ruleBased,
  inProgress,
  hasAuditFlagEnabled
}: {
  hasAuditFlagEnabled: boolean;
  hasClearaCategorization?: boolean;
  applicant: FullApplicant;
  isApplicant: boolean;
  ruleBased?: boolean;
  inProgress: boolean;
  verification: Verification;
}) => {
  const hasPendingCountyReports =
    verification.vendor === "cleara" && verification.has_pending_county_reports;
  return (
    <>
      {hasClearaCategorization ? (
        <>
          <Cell isFirst isDataCell className="relative">
            {verification.summary?.require_approval ? (
              <Lock01 className="stroke-content-100 h-5 w-5" />
            ) : has(verification.summary?.total_felony_offenses) ? (
              <FormattedConvictions
                all={verification.summary.total_felony_offenses.all}
                convicted={verification.summary.total_felony_offenses.convicted}
              />
            ) : hasAuditFlagEnabled ? (
              <AuditSummaryFlag
                audit_summary_flags={applicant.audit_summary_flags}
              />
            ) : (
              "-"
            )}
            {hasPendingCountyReports && (
              <div className="absolute top-0 right-0 text-warning-700 flex gap-2 items-center text-xs p-2 rounded-bl-lg bg-warning-50 border border-alpha-40">
                <Loading02 size="14" className="stroke-warning-700" /> Waiting
                for county search
              </div>
            )}

            <ReportStatusBadge
              verificationStatus={verification.status}
              applicantStatus={applicant.application_status}
              isRequireApproval={verification.summary?.require_approval}
              executionMode={verification.settings?.execution_mode}
            />
          </Cell>
          <Cell isDataCell>
            {verification.summary?.require_approval ? (
              <Lock01 className="stroke-content-100 h-5 w-5" />
            ) : has(verification.summary?.total_misdemeanor_offenses) ? (
              <FormattedConvictions
                all={verification.summary.total_misdemeanor_offenses.all}
                convicted={
                  verification.summary.total_misdemeanor_offenses.convicted
                }
              />
            ) : hasAuditFlagEnabled ? (
              <AuditSummaryFlag
                audit_summary_flags={applicant.audit_summary_flags}
              />
            ) : (
              "-"
            )}
          </Cell>
          <Cell isDataCell>
            {verification.summary?.require_approval ? (
              <Lock01 className="stroke-content-100 h-5 w-5" />
            ) : has(verification.summary?.total_other_criminal_offenses) ? (
              <FormattedConvictions
                all={verification.summary.total_other_criminal_offenses.all}
                convicted={
                  verification.summary.total_other_criminal_offenses.convicted
                }
              />
            ) : hasAuditFlagEnabled ? (
              <AuditSummaryFlag
                audit_summary_flags={applicant.audit_summary_flags}
              />
            ) : (
              "-"
            )}
          </Cell>
        </>
      ) : (
        <>
          <Cell isFirst isDataCell className="relative">
            {verification.summary?.require_approval ? (
              <Lock01 className="stroke-content-100 h-5 w-5" />
            ) : has(verification.summary?.total_criminal_offenses) ? (
              `${verification.summary.total_criminal_offenses} convictions`
            ) : hasAuditFlagEnabled ? (
              <AuditSummaryFlag
                audit_summary_flags={applicant.audit_summary_flags}
              />
            ) : (
              "-"
            )}

            <ReportStatusBadge
              verificationStatus={verification.status}
              applicantStatus={applicant.application_status}
              isRequireApproval={verification.summary?.require_approval}
              executionMode={verification.settings?.execution_mode}
            />
          </Cell>
          <Cell isDataCell>
            {verification.summary?.require_approval ? (
              <Lock01 className="stroke-content-100 h-5 w-5" />
            ) : has(verification.summary?.total_traffic_offenses) ? (
              `${verification.summary.total_traffic_offenses} convictions`
            ) : hasAuditFlagEnabled ? (
              <AuditSummaryFlag
                audit_summary_flags={applicant.audit_summary_flags}
              />
            ) : (
              "-"
            )}
          </Cell>
        </>
      )}
      <Cell
        isDataCell
        check={
          verification.summary?.require_approval ||
          verification.status === VerificationStatus.Failed
            ? null
            : verification.status === VerificationStatus.NoDataAvailable
              ? null
              : isApplicant &&
                  !ruleBased &&
                  !inProgress &&
                  typeof applicant.criteria_check_settings.sex_offender_check
                    ?.importance === "number"
                ? applicant.criteria_check_settings.sex_offender_check.check
                : null
        }
      >
        {verification.summary?.require_approval ? (
          <Lock01 className="stroke-content-100 h-5 w-5" />
        ) : has(verification.summary?.total_sex_offender_offenses) ? (
          `${verification.summary.total_sex_offender_offenses.all} convictions`
        ) : hasAuditFlagEnabled ? (
          <AuditSummaryFlag
            audit_summary_flags={applicant.audit_summary_flags}
          />
        ) : (
          "-"
        )}
      </Cell>
      <Cell isDataCell>
        {verification.summary?.require_approval ? (
          <Lock01 className="stroke-content-100 h-5 w-5" />
        ) : has(verification.summary?.total_ofac_offenses) ? (
          `${verification.summary.total_ofac_offenses} convictions`
        ) : hasAuditFlagEnabled ? (
          <AuditSummaryFlag
            audit_summary_flags={applicant.audit_summary_flags}
          />
        ) : (
          "-"
        )}
      </Cell>
    </>
  );
};
